import React, { memo }         from 'react'
import { animated, useSpring } from 'react-spring'
import styled, { keyframes }   from 'styled-components'

export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  max-width: 100vw;
  overflow: hidden;
`

export const Skewer = memo(({isUpdating, children}) => {
    const styles = useSpring({
        skewX: 0,
        skewY: 0,
        loop: {reverse: true},
        config: {tension: 50},
        to: [
            {
                skewX: 15,
                skewY: 15
            },
            {
                skewX: -15,
                skewY: -15
            },
        ]
    })

    return (isUpdating && (
        <animated.div style={styles} className="skewer">{children}</animated.div>
    )) || children
})

export const Frame = styled.div`
  background-color: ${({$isDark}) => $isDark ? 'black' : 'white'};
  width: min(${({$size}) => $size}px, calc(100vw - 16px), calc(100vh - 16px));
  height: min(${({$size}) => $size}px, calc(100vw - 16px), calc(100vh - 16px));
  overflow: hidden;
`

export const Model = styled.svg`
  width: ${({$dimensions}) => 100 / ($dimensions)}%;
  height: ${({$dimensions}) => 100 / ($dimensions)}%;
`

export const PanelWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  z-index: 1000;
`

const hotspotPulse = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.92);
  }
  10% {
    opacity: 1;
    transform: scale(1);
  }
  30% {
    transform: scale(1.12);
  }
  50% {
    transform: scale(1);
  }
  70% {
    transform: scale(1.12);
  }
  90% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1);
  }
`

export const Hotspot = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 70px;
  height: 70px;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background: transparent;
  display: block;

  &::after {
    content: '';
    position: absolute;
    inset: 12px;
    border: 2px solid white;
    border-radius: 4px;
    box-sizing: border-box;
    pointer-events: none;
    animation: ${hotspotPulse} 6s ease-out 1 forwards;
  }
`

export const Panel = styled.div`
  background-color: white;
  opacity: 0;
  transition: opacity .2s ease-out;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: absolute;
  top: 70px;
  left: 0;
  padding: 8px;
  pointer-events: none;
  width: 236px;
  max-height: calc(100vh - 78px);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  border: 1px solid #d0d0d0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);

  ${PanelWrapper}:hover &, ${PanelWrapper}:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }
`

export const InstructionOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 900;
`

export const InstructionPanel = styled.div`
  width: min(560px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #000;
  color: #fff;
  border: 1px solid #fff;
  padding: 20px;
  box-sizing: border-box;
  pointer-events: auto;
`

export const InstructionBody = styled.div`
  margin-bottom: 16px;
`

export const InstructionButton = styled.button`
  background: #000;
  color: #fff;
  border: 1px solid #fff;
  padding: 8px 12px;
  cursor: pointer;
`

export const Button = styled.button`
  width: 100%;
  min-height: 44px;
  padding: 8px 10px;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .2px;
  border: 1px solid #1f1f1f;
  background: linear-gradient(180deg, #3d3d3d 0%, #1a1a1a 100%);
  color: #fff;
  text-align: center;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.22);
  transition: background .12s ease, transform .06s ease, box-shadow .12s ease;

  &:hover {
    background: linear-gradient(180deg, #4a4a4a 0%, #242424 100%);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.24);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #8ec0ff;
    outline-offset: 1px;
  }
`

export const Field = styled.input`
  width: 100px;
`
